import { useState } from 'react'
import { cn } from '@/lib/utils'

export default function FeatureTabs({
  title = 'How it works',
  description,
  tabs = [],
  className = '',
}) {
  const [activeIndex, setActiveIndex] = useState(0)
  const active = tabs[activeIndex] || tabs[0]

  if (!tabs.length) return null

  return (
    <section className={cn('bg-background py-16 md:py-24', className)}>
      <div className="container mx-auto max-w-7xl px-6">
        <div className="mx-auto mb-10 max-w-3xl text-center md:mb-14">
          <h2 className="mb-4 text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
            {title}
          </h2>
          {description && (
            <p className="text-lg leading-relaxed text-slate-600 md:text-xl">
              {description}
            </p>
          )}
        </div>

        <div className="mx-auto max-w-4xl">
          <div
            role="tablist"
            aria-label={title}
            className="mb-8 flex gap-1 overflow-x-auto border-b border-slate-200 pb-px"
          >
            {tabs.map((tab, index) => {
              const selected = index === activeIndex
              return (
                <button
                  key={tab.id || tab.label}
                  type="button"
                  role="tab"
                  id={`feature-tab-${index}`}
                  aria-selected={selected}
                  aria-controls={`feature-tab-panel-${index}`}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActiveIndex(index)}
                  className={cn(
                    'shrink-0 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors md:px-5 md:text-base',
                    selected
                      ? 'border-primary text-primary'
                      : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-800',
                  )}
                >
                  {tab.label}
                </button>
              )
            })}
          </div>

          {active && (
            <div
              role="tabpanel"
              id={`feature-tab-panel-${activeIndex}`}
              aria-labelledby={`feature-tab-${activeIndex}`}
              className="text-left"
            >
              <h3 className="mb-4 text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">
                {active.title || active.label}
              </h3>
              {active.description && (
                <p className="mb-8 max-w-3xl text-lg leading-relaxed text-slate-600">
                  {active.description}
                </p>
              )}
              {active.bullets?.length > 0 && (
                <ul className="grid gap-4 sm:grid-cols-2">
                  {active.bullets.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <svg
                        className="mt-0.5 h-5 w-5 shrink-0 text-primary"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-base leading-relaxed text-slate-700 md:text-lg">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {active.body && (
                <div className="mt-8 space-y-4 text-base leading-relaxed text-slate-600 md:text-lg">
                  {Array.isArray(active.body)
                    ? active.body.map((paragraph) => <p key={paragraph}>{paragraph}</p>)
                    : <p>{active.body}</p>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
